import React from 'react';
import { cloneDeep, noop } from 'lodash';
import moment from 'moment';

import DndDraggable from 'components/dnd/DndDraggable.component';
import DndDroppable from 'components/dnd/DndDroppable.component';
import Button from 'components/form/Button.component';
import { withTranslation } from 'providers/GILT.provider';
import logger from 'utils/logger.util';
import dnd from 'utils/react/dnd.util';

import PopupModalContainer from 'common/PopupModal';
import AddService from 'onboarding/services/AddService';
import ServiceListItem from 'onboarding/services/ServiceListItem';
import { calculateDifferentRoleExecutions } from 'onboarding/services/utils';

import CN from './CategoryItem.module.scss';

const L = logger('CategoryItem');

const generateEditedServices = ({ newServices, oldServices }) => newServices.filter(({ id }) => oldServices.indexOf(id) < 0).map((obj) => cloneDeep(obj));

class CategoryItem extends React.Component {
  static defaultProps = {
    isAddNewServiceExpanded: false,
    isPostRequestSent: false,
    isExpandable: false,
    onEditServiceSave: noop,
    onAddNewService: noop,
  };
  initState = {
    name: '',
    category: this.props.category.id ? this.props.category.id : '',
    duration: moment('00:45:00', 'HH:mm:ss').format('HH:mm:ss'),
    processing: moment('00:00:00', 'HH:mm:ss').format('HH:mm:ss'),
    variable_pricing: false,
    price: this.props.salonType === 'service' ? '' : '40',
    role_executions: [],
  };

  constructor(props) {
    super(props);
    this.state = {
      serviceToBeAdded: Object.assign({}, this.initState),
      editedServices: L.debug('constructor()', 'ESRV:', []),
      errorMessage: '',
      isAddNewServiceExpanded: false,
      isNewService: false,
      isFirstPriceChange: false,
      isFirstDurationChange: false,
      isFirstProcessingChange: false,
      called: false,
    };
  }

  // noinspection JSCheckFunctionSignatures
  UNSAFE_componentWillReceiveProps(nextProps) {
    L.debug('componentWillReceiveProps()', nextProps, '<-', this.props);

    const newServices = L.info('componentWillReceiveProps()', 'newServices:', nextProps.category.services);
    const newIsExpanded = nextProps.isAddNewServiceExpanded;
    const oldIsExpanded = this.props.isAddNewServiceExpanded;
    const oldEditedServices = this.state.editedServices;

    const editedServices = generateEditedServices({ newServices, oldServices: oldEditedServices });

    // NOTE: due to a weird behavior, this component might get re-rendered without calling componentWillReceiveProps()
    // so, for that case, the flag called will be undefined in render() and editedServices will be empty array
    this.setState({ called: true, editedServices: L.debug('componentWillReceiveProps()', 'ESRV:', editedServices) });

    if (oldIsExpanded && !newIsExpanded) {
      this.resetServiceToBeAdded();
    }
  }

  onServiceNameChange = (value, editedService) => {
    editedService.name = value;
    return true;
  };

  onRoleCheckboxClick = (roleId, editedService) => {
    for (let i = 0; i < editedService.role_executions.length; i += 1) {
      if (editedService.role_executions[i].role_id === roleId) {
        editedService.role_executions.splice(i, 1);
        return true;
      }
    }
    editedService.role_executions.push({
      role_id: roleId,
      price: '',
      duration: '00:45:00',
      processing: '00:00:00',
    });
    return true;
  };

  onDurationChange = (value, editedService) => {
    editedService.duration = moment(value, 'HH:mm').format('HH:mm:ss');
    this.setState({
      serviceToBeAdded: editedService,
      editedServices: L.debug('onDurationChange()', 'ESRV:', [editedService]),
    });
  };

  onProcessingChange = (value, editedService) => {
    editedService.processing = moment(value, 'HH:mm').format('HH:mm:ss');
    this.setState({
      serviceToBeAdded: editedService,
      editedServices: L.debug('onProcessingChange()', 'ESRV:', [editedService]),
    });
  };

  onPriceChange = (value, editedService) => {
    editedService.price = value;
    this.setState({
      serviceToBeAdded: editedService,
      editedServices: L.debug('onPriceChange()', 'ESRV:', [editedService]),
    });
  };

  onPricingChange = (value, editedService) => {
    editedService.variable_pricing = value;
    return true;
  };

  onRolePriceBlur = (value) => {
    const service = Object.assign({}, this.state.serviceToBeAdded);
    if (this.state.isNewService && this.state.isFirstPriceChange) {
      for (let i = 0; i < service.role_executions.length; i += 1) {
        service.role_executions[i].price = value;
        service.price = value;
      }
      this.setState({ serviceToBeAdded: service, isFirstPriceChange: false });
    }
  };

  onRolePriceChange = (roleId, value, editedService) => {
    for (let i = 0; i < editedService.role_executions.length; i += 1) {
      if (editedService.role_executions[i].role_id === roleId) {
        editedService.role_executions[i].price = value;
        editedService.price = value;
        return true;
      }
    }

    return false;
  };

  onRoleApplicationChange = (roleId, value, editedService) => {
    const service = this.state.isNewService ? Object.assign({}, this.state.serviceToBeAdded) : editedService;
    if (this.state.isNewService && this.state.isFirstDurationChange) {
      for (let i = 0; i < service.role_executions.length; i += 1) {
        service.role_executions[i].duration = moment(value, 'HH:mm').format('HH:mm:ss');
        service.duration = value;
      }
      this.setState({ serviceToBeAdded: service, isFirstDurationChange: false });
      return;
    }
    for (let i = 0; i < service.role_executions.length; i += 1) {
      if (service.role_executions[i].role_id === roleId) {
        service.role_executions[i].duration = moment(value, 'HH:mm').format('HH:mm:ss');
        service.duration = value;
        this.setState({ serviceToBeAdded: service });
        return;
      }
    }
  };

  onRoleProcessingChange = (roleId, value, editedService) => {
    const service = this.state.isNewService ? Object.assign({}, editedService) : editedService;
    if (this.state.isNewService && this.state.isFirstProcessingChange) {
      for (let i = 0; i < service.role_executions.length; i += 1) {
        service.role_executions[i].processing = moment(value, 'HH:mm').format('HH:mm:ss');
        service.processing = value;
      }
      this.setState({ serviceToBeAdded: service, isFirstProcessingChange: false });
      return;
    }

    for (let i = 0; i < service.role_executions.length; i += 1) {
      if (service.role_executions[i].role_id === roleId) {
        service.role_executions[i].processing = moment(value, 'HH:mm').format('HH:mm:ss');
        service.processing = value;
        this.setState({ editedServices: L.debug('onRoleProcessingChange()', i, 'ESRV:', [service]) });
        return;
      }
    }
  };

  onAddNewServiceBtnClick = () => {
    if (this.props.onAddNewService && this.checkIfValidService(this.state.serviceToBeAdded)) {
      this.props.onAddNewService(this.state.serviceToBeAdded, this.props.category);
      this.setState({ isAddNewServiceExpanded: false });
    }
  };

  showAddServiceDropdown = () => {
    this.setState({
      isAddNewServiceExpanded: true,
      isFirstDurationChange: true,
      isFirstProcessingChange: true,
      isFirstPriceChange: true,
      isNewService: true,
    });
    this.resetServiceToBeAdded();
  };

  checkIfValidService = (service) => {
    L.debug('checkIfValidService()', service);

    if (service.name && (this.props.salonType === 'service' ? service.price !== '' : true) && service.duration !== '00:00:00') {
      return !service.role_executions.some((role_execution) => role_execution.price === '');
    }

    return false;
  };

  onCategoryChangeInSettings = (service, category) => {
    if (!category.id) {
      return;
    }

    service.category = category.id;
  };

  handleEditServiceSave = (editedService, currentService) => {
    if (this.props.onEditServiceSave) {
      this.props.onEditServiceSave(editedService, calculateDifferentRoleExecutions(editedService, currentService));
    }
  };

  hideError = () => {
    this.setState({ errorMessage: '' });
  };

  // This is needed because just Object.assign doesn't work well enough for objects with arrays
  resetServiceToBeAdded() {
    const newServiceToBeAdded = Object.assign({}, this.initState);
    newServiceToBeAdded.role_executions = [];

    this.setState({ serviceToBeAdded: newServiceToBeAdded });
  }
  render() {
    const {
      settingsSavedServicesIDs,
      isAddNewServiceExpanded: isExpandedFromProps,
      isExpandable,
      onDelete,
      expandedServicesIDs,
      category,
      onEditServiceSave,
      onEdit,
      quickSetupLayout,
      dragAndDropEnabled,
      settingsErrorServicesIDs,
      t,
    } = this.props;

    const {
      called,
      editedServices: editedServicesFromState,
      serviceToBeAdded: serviceToBeAddedFromState,
      isAddNewServiceExpanded: isExpanedFromState,
      errorMessage: errorMessageFromState,
    } = this.state;

    const { services, name: categoryName, id: categoryId } = category;
    const buttonName = services?.length > 0 ? t('composite.categoryItem.addAnotherServiceBtn') : t('composite.categoryItem.addAnotherServiceBtn', { category: categoryName });

    const editedServices = L.info('render()', { called, services }, 'editedServices:', editedServicesFromState);

    const categoryJsx = (
      <div data-bem="CategoryItem__category--jsx" className="rounded-border-wrap dashed">
        {services &&
          services.map((service, i) => {
            const serviceId = service.id;
            const expanded = expandedServicesIDs.indexOf(serviceId) !== -1;
            const isSaved = settingsSavedServicesIDs.indexOf(serviceId) !== -1;
            const errorMessage = settingsErrorServicesIDs.find(({ id }) => serviceId === id);
            const newEditedServices = editedServices.map((srvs) => Object.assign({}, srvs));
            const editedService = newEditedServices.find(({ id }) => serviceId === id);

            L.info('render()', 'map()', i, serviceId, editedService, { editedServices, service, services, category });

            const serviceJSX = (
              <div data-bem="CategoryItem__service--jsx" className="obs-edit-container cfix">
                <ServiceListItem
                  expandable={isExpandable}
                  showDotsMenu={!expanded}
                  quickSetupLayout={quickSetupLayout}
                  itemName={service.name}
                  isSaved={isSaved}
                  errorMessage={errorMessage && errorMessage.message}
                  onEdit={() => onEdit(service, category)}
                  onDelete={() => onDelete(serviceId)}
                />
                {expanded && (
                  <div data-bem="CategoryItem__service-container--expanded">
                    <AddService
                      serviceId={serviceId}
                      service={editedService}
                      category={category}
                      quickSetupLayout={quickSetupLayout}
                      isNameSelected
                      serviceTemplates={[]}
                      settingsLayout
                      onServiceNameChange={(value) => {
                        if (editedService && this.onServiceNameChange(value, editedService)) {
                          this.setState({
                            editedServices: L.debug('onServiceNameChange()', 'ESRV:', newEditedServices),
                          });
                        }
                      }}
                      onCategoryChange={noop}
                      onCategoryChangeInSettings={this.onCategoryChangeInSettings}
                      onRoleCheckboxChange={(roleId) => {
                        if (editedService && this.onRoleCheckboxClick(roleId, editedService)) {
                          this.setState({
                            editedServices: L.debug('onRoleCheckboxChange()', 'ESRV:', newEditedServices),
                          });
                        }
                      }}
                      onRolePriceChange={(roleId, value) => {
                        if (editedService && this.onRolePriceChange(roleId, value, editedService)) {
                          this.setState({
                            editedServices: L.debug('onRolePriceChange()', 'ESRV:', newEditedServices),
                          });
                        }
                      }}
                      onRolePriceBlur={this.onRolePriceBlur}
                      onRoleApplicationChange={(roleId, value) => {
                        if (editedService && this.onRoleApplicationChange(roleId, value, editedService)) {
                          this.setState({
                            editedServices: L.debug('onRoleApplicationChange()', 'ESRV:', newEditedServices),
                          });
                        }
                      }}
                      onRoleProcessingChange={(roleId, value) => {
                        if (editedService && this.onRoleProcessingChange(roleId, value, editedService)) {
                          this.setState({
                            editedServices: L.debug('onRoleProcessingChange()', 'ESRV:', newEditedServices),
                          });
                        }
                      }}
                      onDurationChange={(value) => {
                        if (editedService && this.onDurationChange(value, editedService)) {
                          this.setState({ editedServices: L.debug('onDurationChange()', 'ESRV:', newEditedServices) });
                        }
                      }}
                      onProcessingChange={(value) => {
                        if (editedService && this.onProcessingChange(value, editedService)) {
                          this.setState({
                            editedServices: L.debug('onProcessingChange()', 'ESRV:', newEditedServices),
                            serviceToBeAdded: newEditedServices[0],
                          });
                        }
                      }}
                      onPriceChange={(value) => {
                        if (editedService && this.onPriceChange(value, editedService)) {
                          this.setState({ editedServices: L.debug('onPriceChange()', 'ESRV:', newEditedServices) });
                        }
                      }}
                      onPrincingChange={(value) => {
                        if (editedService && this.onPricingChange(value, editedService)) {
                          this.setState({ editedServices: L.debug('onPrincingChange()', 'ESRV:', newEditedServices) });
                        }
                      }}
                      addNewService={noop}
                      selectTemplate={noop}
                    />

                    <div className={CN.buttonContainer}>
                      <Button
                        variant="primary"
                        onClick={() => {
                          if (editedService && onEditServiceSave) {
                            this.handleEditServiceSave(editedService, service);
                          }
                        }}
                      >
                        <span className={CN.buttonGap}>{t('common.buttons.saveChanges')}</span>
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );

            return (
              <div key={serviceId} id={serviceId}>
                {errorMessageFromState && (
                  <PopupModalContainer popupTitle={t('common.error')} popupMessage={errorMessageFromState} buttonText={t('common.buttons.okay')} buttonCallback={this.hideError} />
                )}
                {dragAndDropEnabled ? (
                  <DndDraggable tag="CategoryItem--dnd-source" draggableId={dnd.dragService({ categoryId, serviceId })} index={i}>
                    {({ dragHandleProps, draggableProps, innerRef }) => (
                      <div data-bem="CategoryItem--el-source" ref={innerRef} {...draggableProps} {...dragHandleProps} style={draggableProps.style}>
                        {serviceJSX}
                      </div>
                    )}
                  </DndDraggable>
                ) : (
                  <div>{serviceJSX}</div>
                )}
              </div>
            );
          })}
        {!isExpandedFromProps && !quickSetupLayout && (
          <div className={CN.buttonContainer}>
            <Button variant="primaryForce" onClick={this.showAddServiceDropdown}>
              <span className={CN.buttonGap}>{buttonName}</span>
            </Button>
          </div>
        )}
        {(isExpandedFromProps || isExpanedFromState) && (
          <div className="first-service-container">
            <button className="close-btn" onClick={() => this.setState({ isAddNewServiceExpanded: false })} />

            <AddService
              service={serviceToBeAddedFromState}
              category={category}
              isNameSelected
              serviceTemplates={[]}
              settingsLayout
              onServiceNameChange={(value) => {
                const serviceToBeAdded = Object.assign({}, serviceToBeAddedFromState);
                if (this.onServiceNameChange(value, serviceToBeAdded)) {
                  this.setState({ serviceToBeAdded });
                }
              }}
              onCategoryChange={noop}
              onCategoryChangeInSettings={this.onCategoryChangeInSettings}
              onRoleCheckboxChange={(roleId) => {
                const serviceToBeAdded = Object.assign({}, serviceToBeAddedFromState);
                if (this.onRoleCheckboxClick(roleId, serviceToBeAdded)) {
                  this.setState({ serviceToBeAdded });
                }
              }}
              onRolePriceChange={(roleId, value) => {
                const serviceToBeAdded = Object.assign({}, serviceToBeAddedFromState);
                if (this.onRolePriceChange(roleId, value, serviceToBeAdded)) {
                  this.setState({ serviceToBeAdded });
                }
              }}
              onRolePriceBlur={this.onRolePriceBlur}
              onRoleApplicationChange={(roleId, value) => {
                const serviceToBeAdded = Object.assign({}, serviceToBeAddedFromState);
                if (this.onRoleApplicationChange(roleId, value, serviceToBeAdded)) {
                  this.setState({ serviceToBeAdded });
                }
              }}
              onRoleProcessingChange={(roleId, value) => {
                const serviceToBeAdded = Object.assign({}, serviceToBeAddedFromState);
                if (this.onRoleProcessingChange(roleId, value, serviceToBeAdded)) {
                  this.setState({ serviceToBeAdded });
                }
              }}
              onDurationChange={(value) => {
                const serviceToBeAdded = Object.assign({}, serviceToBeAddedFromState);
                if (this.onDurationChange(value, serviceToBeAdded)) {
                  this.setState({ serviceToBeAdded });
                }
                return true;
              }}
              onProcessingChange={(value) => {
                const serviceToBeAdded = Object.assign({}, serviceToBeAddedFromState);
                if (this.onProcessingChange(value, serviceToBeAdded)) {
                  this.setState({ serviceToBeAdded });
                }
                return true;
              }}
              onPriceChange={(value) => {
                const serviceToBeAdded = Object.assign({}, serviceToBeAddedFromState);
                if (this.onPriceChange(value, serviceToBeAdded)) {
                  this.setState({ serviceToBeAdded });
                }
                return true;
              }}
              onPrincingChange={(value) => {
                const serviceToBeAdded = Object.assign({}, serviceToBeAddedFromState);
                if (this.onPricingChange(value, serviceToBeAdded)) {
                  this.setState({ serviceToBeAdded });
                }
              }}
              addNewService={noop}
              selectTemplate={noop}
            />
            <div>
              {serviceToBeAddedFromState.name.length <= 0 && <span className={CN.error}>{t('composite.categoryItem.serviceNameErrorMsg')}</span>}
              <Button variant="primaryForce" onClick={this.onAddNewServiceBtnClick} disabled={!this.checkIfValidService(serviceToBeAddedFromState) || serviceToBeAddedFromState.name.length <= 0}>
                <span className={CN.buttonGap}>{t('composite.categoryItem.saveNewServButton')}</span>
              </Button>
            </div>
          </div>
        )}
      </div>
    );

    return (
      <div data-bem="CategoryItem" className="category-item">
        {!isExpandable && (
          <div className="category-wrap">
            <div style={{ backgroundColor: category.color }} className="category-bullet" />
            <div className="category-text">{categoryName}</div>
          </div>
        )}

        {dragAndDropEnabled ? (
          <DndDroppable tag="CategoryItem--dnd-target" droppableId={dnd.dropService({ categoryId })}>
            {({ innerRef, placeholder }) => (
              <div data-bem="CategoryItem--el-target" ref={innerRef}>
                {placeholder}
                {categoryJsx}
              </div>
            )}
          </DndDroppable>
        ) : (
          <div>{categoryJsx}</div>
        )}
      </div>
    );
  }
}

const CategoryItemHoc = withTranslation()(CategoryItem);

export default CategoryItemHoc;
